import { useEffect, useRef, useState } from 'react';
import type { LiveGame } from '@edh/shared';
import {
  PLACEMENT_GAMES,
  deckLabel,
  display,
  draftOrder,
  draftView,
  isRanked,
  roman,
} from '@edh/shared';
import { DeckThumb } from './DeckThumb';
import { RankedAvatar } from './RankedAvatar';
import { Sheet } from './Sheet';
import { draftAct } from '../lib/live';
import { useLookups, useStore } from '../lib/store';

/**
 * The draft itself: everybody bans one, then everybody takes one.
 *
 * Both halves run round the table from whoever the wheel landed on, in the same
 * direction — the order the rest of the app already runs in. See `draftView`
 * for why it stopped snaking.
 *
 * Every phone shows the same screen and only the seat whose turn it is can act,
 * with the host able to act for anybody — the same rule as the chairs, and for
 * the same reason: somebody at a Commander table is always without a phone.
 * Acting for somebody else names them and asks first, because a draft has no
 * undo and the tap is not on your own behalf.
 */
export function Draft({ game, onChange }: { game: LiveGame; onChange: (g: LiveGame) => void }) {
  const { state, me } = useStore();
  const { pilot, deck } = useLookups();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * A deck the host has tapped on somebody else's turn, waiting to be confirmed.
   *
   * Only for the proxy case. A ban cannot be taken back — there is no undo in a
   * draft and adding one would mean unwinding a pick that may already have been
   * made on top of it — so the one tap that is permanent *and* on behalf of
   * somebody else is the one tap worth interrupting. Your own turn stays a
   * single tap, because it is your deck and your mistake to make.
   */
  const [confirming, setConfirming] = useState<string | null>(null);
  /**
   * A deck you have tapped once on your own ban, waiting for the second tap.
   *
   * A ban is the one act in a draft that takes something away from everybody
   * and cannot be given back, and it is made by tapping a grid of small cards
   * on a phone at a table. Your own pick gets a single tap because the worst it
   * can do is hand you a deck you did not mean to play; a ban is spent on the
   * table's behalf.
   *
   * Two taps on the same card rather than a dialog, because a dialog for the
   * six-to-nine acts of a draft is a dialog people learn to dismiss without
   * reading. The card says what the second tap will do while it waits.
   */
  const [arming, setArming] = useState<string | null>(null);
  const armTimer = useRef(0);
  /**
   * The game whose bans have been read, so the summary appears once.
   *
   * On the device rather than in the document: it is a fact about what this
   * screen has shown, and the other phones each need their own answer — the
   * same reasoning as the turn-order roll.
   */
  const [readBans, setReadBans] = useState<string | null>(null);

  const acted = game.draft?.actions.length ?? 0;
  /*
   * Anything half-tapped is forgotten the moment the draft moves on — the turn
   * passing, or somebody else taking that very deck. An armed card surviving
   * into a later turn is a tap somebody made about a different decision.
   */
  useEffect(() => setArming(null), [acted]);
  useEffect(() => () => window.clearTimeout(armTimer.current), []);

  const view = draftView(game);
  if (!view) return null;

  const order = draftOrder(game);
  const active = view.seat === null ? null : game.seats[view.seat];
  const mine = Boolean(active?.pilotId && active.pilotId === me?.id);
  const host = game.hostPilotId === me?.id;
  /*
   * The host tapping for somebody else — and never silently.
   *
   * The ability is deliberate and the same one the chairs have: not everyone at
   * a Commander table is holding a phone, and a draft that only the seat itself
   * could act in would make those people unplayable. What was wrong was the
   * screen, which read `mine || host` and so told the host "Your ban" on
   * everybody's turn. A host who tapped then banned out of somebody else's
   * pool believing it was their own.
   *
   * So the ability stays and the claim goes: it is only ever "your" turn when
   * it is, and acting for somebody else says whose turn it is and that you are
   * doing it for them.
   */
  const proxy = !mine && host && view.seat !== null;
  const canAct = mine || proxy;
  const activeName = pilot(active?.pilotId ?? '')?.name ?? 'the next player';

  async function act(deckId: string) {
    if (busy || view === null || view.stage === 'done') return;
    setBusy(true);
    setError(null);
    try {
      setConfirming(null);
      setArming(null);
      onChange(await draftAct(game.id, view.stage, deckId));
    } catch (err) {
      // Usually somebody beat you to that deck, which the next poll explains
      // anyway — but a tap that did nothing needs a reason.
      setError(err instanceof Error ? err.message : 'that did not work');
    } finally {
      setBusy(false);
    }
  }

  const banning = view.stage === 'ban';

  /**
   * What a tap on a deck means, which depends on whose turn it is and on what
   * the tap would spend.
   *
   *   your pick        one tap
   *   your ban         two taps on the same card
   *   anybody else's   a dialog naming them, whichever it is
   */
  function tap(deckId: string) {
    if (proxy) {
      setConfirming(deckId);
      return;
    }
    if (!banning || arming === deckId) {
      void act(deckId);
      return;
    }
    setArming(deckId);
    // Long enough to look up and think, short enough that a card is never
    // still armed by the time somebody comes back to the phone.
    window.clearTimeout(armTimer.current);
    armTimer.current = window.setTimeout(() => setArming(null), 5000);
  }

  return (
    <div className="draft">
      <div className="section-head" style={{ marginTop: 0 }}>
        <h2>{banning ? 'Bans' : 'Picks'}</h2>
        <span className="hint num">{view.remaining} left</span>
      </div>

      {/* The running order, so nobody has to be told twice when their turn
          is. The same order for both halves, which is the point of it. */}
      <div className="draft-order">
        {order.map((seat) => {
          const p = pilot(game.seats[seat]?.pilotId ?? '');
          const done = banning
            ? game.draft!.actions.some((a) => a.kind === 'ban' && a.seat === seat)
            : Boolean(view.taken[seat]);
          return (
            <span
              key={seat}
              className={`draft-turn${seat === view.seat ? ' now' : ''}${done ? ' done' : ''}`}
            >
              <RankedAvatar pilot={p} size={30} />
              <span className="draft-turn-name">{p?.name ?? '—'}</span>
            </span>
          );
        })}
      </div>

      <p className={`hint${proxy ? ' draft-proxy' : ''}`}>
        {view.stage === 'done'
          ? 'Everybody has a deck.'
          : mine
            ? banning
              ? 'Your ban — take one out of the pool.'
              : 'Your pick — this is what you are playing.'
            : proxy
              ? `${activeName}'s ${banning ? 'ban' : 'pick'}. Tap for them only if they are not on their phone.`
              : `Waiting on ${activeName}.`}
      </p>

      {error && <div className="banner">{error}</div>}

      <div className="draft-pool">
        {game.draft!.dealt.map((id) => {
          const d = deck(id);
          if (!d) return null;
          const banned = view.banned.includes(id);
          const takenBy = Object.entries(view.taken).find(([, deckId]) => deckId === id)?.[0];
          const gone = banned || takenBy !== undefined;
          const owner = takenBy === undefined ? null : pilot(game.seats[Number(takenBy)]?.pilotId ?? '');
          return (
            <button
              key={id}
              className={`draft-card${banned ? ' banned' : ''}${
                takenBy !== undefined ? ' taken' : ''
              }${arming === id ? ' arming' : ''}`}
              disabled={gone || !canAct || busy}
              onClick={() => tap(id)}
            >
              <DeckThumb deck={d} size={64} />
              <span className="draft-card-name">{deckLabel(d)}</span>
              {/*
                The rating, because this is the one screen where it is a
                decision rather than a fact. Nothing during placement pretends
                to be a rating — the same rule the seat cards use — and the
                bracket sits beside it, since a deck three brackets up is a
                different proposition from one thirty points up.
              */}
              <span className="draft-card-rating num">
                {isRanked(d.placementGames)
                  ? display(d.elo)
                  : `${d.placementGames}/${PLACEMENT_GAMES}`}
                {d.powerLevel && (
                  <span className="draft-card-bracket">{roman(d.powerLevel)}</span>
                )}
              </span>
              {/* What happened to it, rather than only that something did. */}
              {banned && <span className="draft-card-tag banned">banned</span>}
              {owner && <span className="draft-card-tag taken">{owner.name}</span>}
              {/* Says what the next tap does, on the card it will do it to. */}
              {arming === id && <span className="draft-card-tag arming">tap to ban</span>}
            </button>
          );
        })}
      </div>

      {/*
        The spare is worth naming. With two decks a player and one over there is
        always one nobody took, and a card left lit at the end reads like a bug
        unless the screen says otherwise.
      */}
      {view.stage === 'done' && view.available.length > 0 && (
        <p className="hint">
          {view.available.length === 1 ? 'One deck went unplayed.' : 'Some decks went unplayed.'}
        </p>
      )}

      {state.matches.length === 0 && (
        <p className="hint">Ban first, then pick. Whoever banned first picks last.</p>
      )}

      {/*
        A full stop between the two halves of the draft.

        Bans and picks are the same grid of the same cards, and without
        something between them the moment the pool stops shrinking and starts
        being shared out goes by unannounced — people carried on tapping and
        found they had taken a deck when they meant to remove one. It has to be
        closed rather than fading, because the point is that it interrupts.

        It is also the first moment the draft is worth reading: what everybody
        took out, and what that leaves.
      */}
      {view.stage === 'pick' && readBans !== game.id && (
        <Sheet label="Bans" onClose={() => setReadBans(game.id)}>
          <div className="section-head" style={{ marginTop: 0 }}>
            <h2>Banned</h2>
          </div>
          <div className="list">
            {game.draft!.actions
              .filter((a) => a.kind === 'ban')
              .map((a) => {
                const p = pilot(game.seats[a.seat]?.pilotId ?? '');
                const d = deck(a.deckId);
                return (
                  <div className="list-item" key={a.id}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <RankedAvatar pilot={p} size={28} />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block' }}>{d ? deckLabel(d) : 'a deck'}</span>
                        <span className="sub">{p?.name ?? 'somebody'}</span>
                      </span>
                    </span>
                    {d && (
                      <span className="num sub">
                        {isRanked(d.placementGames)
                          ? display(d.elo)
                          : `${d.placementGames}/${PLACEMENT_GAMES}`}
                      </span>
                    )}
                  </div>
                );
              })}
          </div>

          {/*
            What is left, strongest first — the closest thing to odds that
            exists before anybody has a deck. Nobody can be given a win chance
            yet: until the picks land there is no pairing to compute one from.
          */}
          <div className="section-head">
            <h2>Still on the table</h2>
            <span className="hint num">{view.available.length}</span>
          </div>
          <div className="list">
            {[...view.available]
              .map((id) => deck(id))
              .filter((d): d is NonNullable<typeof d> => Boolean(d))
              .sort((a, b) => b.elo - a.elo)
              .map((d) => (
                <div className="list-item" key={d.id}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <DeckThumb deck={d} size={28} />
                    <span
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {deckLabel(d)}
                    </span>
                  </span>
                  <span className="num sub">
                    {isRanked(d.placementGames)
                      ? display(d.elo)
                      : `${d.placementGames}/${PLACEMENT_GAMES}`}
                  </span>
                </div>
              ))}
          </div>

          {/*
            Numbered rather than listed in a sentence.

            This is the one thing on the sheet somebody has to act on — it is
            the answer to "when am I up", and reading it out of a comma-
            separated line means counting names. The numbers do the counting.
          */}
          <div className="section-head">
            <h2>Pick order</h2>
          </div>
          <div className="pick-order">
            {order.map((seat, i) => {
              const p = pilot(game.seats[seat]?.pilotId ?? '');
              return (
                <span className={`pick-order-seat${p?.id === me?.id ? ' mine' : ''}`} key={seat}>
                  <span className="pick-order-n num">{i + 1}</span>
                  <RankedAvatar pilot={p} size={30} />
                  <span className="pick-order-name">{p?.name ?? '—'}</span>
                  {p?.id === me?.id && <span className="pip gold">you</span>}
                </span>
              );
            })}
          </div>
          <p className="hint" style={{ marginTop: 8 }}>
            The same order the bans ran in.
          </p>

          <button className="btn primary block" onClick={() => setReadBans(game.id)}>
            Start picking
          </button>
        </Sheet>
      )}

      {/*
        Naming both the deck and the person, because those are the two things a
        mis-tap gets wrong and the only two worth reading before confirming.
      */}
      {confirming && (
        <Sheet
          label={banning ? 'Ban for somebody else' : 'Pick for somebody else'}
          onClose={() => setConfirming(null)}
        >
          <p style={{ marginTop: 0 }}>
            {banning ? 'Ban ' : 'Give '}
            <strong>{deckLabel(deck(confirming)!)}</strong>
            {banning ? ' on behalf of ' : ' to '}
            <strong>{activeName}</strong>?
          </p>
          <p className="hint">
            {banning
              ? 'It leaves the pool for everybody, and a draft has no undo.'
              : 'This is the deck they will play, and a draft has no undo.'}
          </p>
          <div className="btn-row">
            <button className="btn ghost" onClick={() => setConfirming(null)} disabled={busy}>
              Cancel
            </button>
            <button className="btn primary" onClick={() => void act(confirming)} disabled={busy}>
              {busy ? <span className="spinner" /> : banning ? 'Ban it' : 'Give it'}
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
